import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useSessionRoles } from '../auth/use-session-roles';
import { fetchUserById } from './api';
import { BiddingModeCard } from './components/bidding-mode-card';
import { ClientBidsTab } from './components/client-bids-tab';
import { CLIENT_TABS, ClientDetailTabs, type ClientTab } from './components/client-detail-tabs';
import { ClientVerificationCard } from './components/client-verification-card';
import { ClientVisitsTab } from './components/client-visits-tab';
import { ClientWonTab } from './components/client-won-tab';
import { DeleteClientAccountAction } from './components/delete-client-account-action';
import { UserDocumentsTab } from './components/user-documents-tab';
import { UserFinancesTab } from './components/user-finances-tab';
import { UserInfoTab } from './components/user-info-tab';
import { UserProfileHeader } from './components/user-profile-header';
import { UserRolesCard } from './components/user-roles-card';

/** The tab that opens by default — and the one the URL omits. */
const DEFAULT_CLIENT_TAB: ClientTab = 'verification';

/** `/clients/$id?tab=` — anything that is not a known tab falls back to «Верифікація». */
export function parseClientTab(value: unknown): ClientTab {
  return typeof value === 'string' && (CLIENT_TABS as readonly string[]).includes(value)
    ? (value as ClientTab)
    : DEFAULT_CLIENT_TAB;
}

/**
 * «Клієнт» (`/clients/$id`) — `Redesign-D-Admin{,-Mobile}`.
 *
 * The densest screen in the artboard, and the ordering is the argument: the
 * header says who, the tab strip says which facet, and the default facet is
 * «Верифікація» because that is the thing that blocks a client from bidding and
 * therefore the reason an operator opened this page at all.
 *
 * The two verification cards sit side by side from `lg` (the artboard's
 * `auto-fit, minmax(340px, 1fr)`) and stack below it.
 *
 * The tab lives in the URL (`?tab=finances`, BUG-32) — the same pattern as
 * «Угоди» — so a reload, a shared link and the back button return to the facet
 * the operator was on. The default facet is omitted from the URL.
 */
export function ClientDetailPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const search = useSearch({ strict: false }) as { tab?: unknown };
  const navigate = useNavigate();
  const { isSuperadmin } = useSessionRoles();
  // «Відвідування» is superuser only (owner decision 2026-10-13): a plain admin
  // neither sees the tab nor opens it from a `?tab=visits` link.
  const tabs = isSuperadmin ? CLIENT_TABS : CLIENT_TABS.filter((t) => t !== 'visits');
  const requested = parseClientTab(search.tab);
  const tab = tabs.includes(requested) ? requested : DEFAULT_CLIENT_TAB;

  const setTab = (next: ClientTab) => {
    void navigate({
      to: '/clients/$id',
      params: { id: id ?? '' },
      search: next === DEFAULT_CLIENT_TAB ? {} : { tab: next },
      replace: true,
    });
  };

  const query = useQuery({
    queryKey: ['user', id],
    queryFn: () => fetchUserById(id as string),
    enabled: !!id,
  });

  if (query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403)) {
    return <AccessDenied />;
  }

  const user = query.data;

  return (
    <>
      <Link
        to="/clients"
        className="inline-flex w-fit items-center gap-1.5 text-[13px] leading-[17px] text-cf-ink-muted hover:text-cf-ink"
      >
        <ArrowLeft size={14} strokeWidth={1.75} />
        {t('clients.detail.back')}
      </Link>

      {query.isLoading ? (
        <Notice text={t('common.loading.default')} />
      ) : !user ? (
        <Notice text={t('clients.detail.notFound')} />
      ) : (
        <>
          <UserProfileHeader
            user={user}
            actions={
              <DeleteClientAccountAction
                user={user}
                onDeleted={() => void navigate({ to: '/clients' })}
              />
            }
          />
          <ClientDetailTabs active={tab} onChange={setTab} tabs={tabs} />

          {tab === 'verification' && (
            // The artboard's two columns: the verification work on the left,
            // «Режим ставок» as a sticky aside (flex 1 1 340px, ≤ 420px) that
            // wraps under it on narrow screens.
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:gap-5">
              <div className="flex min-w-0 flex-col gap-3 lg:flex-[1_1_560px] lg:gap-5">
                {/* «Статус верифікації» spans the column. The separate «Рівень
                    верифікації» ladder card was removed (owner, 2026-09-28);
                    pending documents are approved on «Документи». */}
                <ClientVerificationCard user={user} />
                <UserDocumentsTab user={user} />
              </div>
              {user._id && (
                <aside className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-6 lg:max-w-[420px] lg:flex-[1_1_340px] lg:gap-5">
                  <BiddingModeCard userId={user._id} />
                  {/* «Ролі» — superuser only: grants admin / superadmin / local_*. */}
                  {isSuperadmin && <UserRolesCard user={user} />}
                </aside>
              )}
            </div>
          )}
          {tab === 'bids' && <ClientBidsTab user={user} />}
          {tab === 'won' && <ClientWonTab user={user} />}
          {tab === 'finances' && <UserFinancesTab user={user} />}
          {tab === 'visits' && user._id && <ClientVisitsTab userId={user._id} />}
          {tab === 'personal' && <UserInfoTab user={user} />}
        </>
      )}
    </>
  );
}

function Notice({ text }: { text: string }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle">
      {text}
    </section>
  );
}
